import React, { useEffect, useMemo, useCallback, memo, useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  Chip,
  IconButton,
  Button,
  TextField,
  Collapse,
  LinearProgress,
  Paper,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Drawer,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import HomeIcon from "@mui/icons-material/Home"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import RefreshIcon from "@mui/icons-material/Refresh"
import FactoryIcon from "@mui/icons-material/Factory"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import PendingActionsIcon from "@mui/icons-material/PendingActions"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import DoneAllIcon from "@mui/icons-material/DoneAll"
import CategoryIcon from "@mui/icons-material/Category"
import BuildIcon from "@mui/icons-material/Build"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import WarningIcon from "@mui/icons-material/Warning"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"

import {
  fetchSections,
  fetchSectionTasks,
  clearSectionsCache,
  setActiveSection,
} from "store/sections/actions"
import {
  selectSections,
  selectSectionTasks,
  selectSectionsLoading,
  selectTasksLoading,
  selectTasksBySection,
  selectSectionTaskStats,
} from "store/sections/reducer"
import {
  getMaterials,
  getProducts,
  patchTask,
  patchProduct,
  postTask,
  postInvoice,
} from "helpers/api_helper"

// Status chip colors
const getStatusColor = (status) => {
  switch (status) {
    case "PENDING":
      return { bg: "#fef3c7", color: "#d97706", label: "Pending" }
    case "STOCK-RECEIVED":
      return { bg: "#dbeafe", color: "#3b82f6", label: "Stock Received" }
    case "FINISHED":
      return { bg: "#dcfce7", color: "#16a34a", label: "Finished" }
    default:
      return { bg: "#f3f4f6", color: "#6b7280", label: status }
  }
}

// Memoized Status Chip
const StatusChip = memo(({ status, t }) => {
  const statusInfo = getStatusColor(status)
  return (
    <Chip
      label={t(statusInfo.label)}
      size="small"
      sx={{
        bgcolor: statusInfo.bg,
        color: statusInfo.color,
        fontWeight: 500,
        fontSize: "0.75rem",
      }}
    />
  )
})

// Memoized Analytics Header
const AnalyticsHeader = memo(({ stats, sectionName, t }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: "#8b5cf6", color: "#fff", width: 48, height: 48 }}>
            <FactoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#111827">
              {sectionName}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Production Section")}
            </Typography>
          </Box>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" spacing={2}>
          <Chip
            icon={<PendingActionsIcon sx={{ fontSize: 16 }} />}
            label={`${stats.pending} ${t("Pending")}`}
            sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 500 }}
          />
          <Chip
            icon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
            label={`${stats.stockReceived} ${t("In Progress")}`}
            sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 500 }}
          />
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={`${stats.finished} ${t("Finished")}`}
            sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 500 }}
          />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
))

const MuiSectionPage = () => {
  const { section: sectionName } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get order filter from URL query params
  const orderFilter = searchParams.get("order")

  document.title = `${sectionName}${orderFilter ? ` - #${orderFilter}` : ""} - ${t("Section")} | FlexFlow ERP`

  // Redux state
  const sections = useSelector(selectSections)
  const allTasks = useSelector(selectSectionTasks)
  const sectionsLoading = useSelector(selectSectionsLoading)
  const tasksLoading = useSelector(selectTasksLoading)

  // Get tasks for this section (filtered by order if specified)
  const sectionTasks = useMemo(() => {
    let tasks = allTasks.filter((task) => task.type === sectionName)
    if (orderFilter) {
      tasks = tasks.filter((task) => String(task.orderref) === orderFilter)
    }
    return tasks
  }, [allTasks, sectionName, orderFilter])

  // Clear order filter
  const clearOrderFilter = () => {
    searchParams.delete("order")
    setSearchParams(searchParams)
  }

  // Get stats for this section
  const stats = useMemo(() => ({
    total: sectionTasks.length,
    pending: sectionTasks.filter((t) => t.status === "PENDING").length,
    stockReceived: sectionTasks.filter((t) => t.status === "STOCK-RECEIVED").length,
    finished: sectionTasks.filter((t) => t.status === "FINISHED").length,
  }), [sectionTasks])

  // Local state
  const [selectedTask, setSelectedTask] = useState(null)
  const [selectedTaskMats, setSelectedTaskMats] = useState([])
  const [taskComments, setTaskComments] = useState("")
  const [operationMode, setOperationMode] = useState("normal")
  const [materialsList, setMaterialsList] = useState([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [materialSearch, setMaterialSearch] = useState("")
  const [replacingMaterialIndex, setReplacingMaterialIndex] = useState(null)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [expandedTask, setExpandedTask] = useState(true)

  const loading = sectionsLoading || tasksLoading

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchSections())
    dispatch(fetchSectionTasks())
    dispatch(setActiveSection(sectionName))
    getMaterials().then(setMaterialsList)
  }, [dispatch, sectionName])

  // Refresh data
  const handleRefresh = useCallback(() => {
    dispatch(clearSectionsCache())
    dispatch(fetchSections(true))
    dispatch(fetchSectionTasks(true))
  }, [dispatch])

  // Show snackbar
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }, [])

  // Get product stock
  const getProductStock = useCallback(async (id) => {
    const product = await getProducts(id)
    return product?.[0]?.stock || 0
  }, [])

  // Update materials with current stock
  const updateMaterialsStock = useCallback(async (materials) => {
    if (!materials || materials.length === 0) return []
    const updated = await Promise.all(
      materials.map(async (mat) => {
        const stock = await getProductStock(mat.id)
        return { ...mat, stock }
      })
    )
    return updated
  }, [getProductStock])

  // Handle task selection
  const handleTaskSelect = useCallback(async (task) => {
    setSelectedTask(task)
    setTaskComments(task.comments || "")
    setExpandedTask(true)
    try {
      const materials = JSON.parse(task.materials || "[]")
      const updatedMats = await updateMaterialsStock(materials)
      setSelectedTaskMats(updatedMats)
    } catch (error) {
      console.error("Error parsing materials:", error)
      setSelectedTaskMats([])
    }
  }, [updateMaterialsStock])

  // Calculate required quantity
  const calculateRequired = useCallback((mat) => {
    return parseFloat(selectedTask?.quantity || 0) * parseFloat(mat.quantity || 0)
  }, [selectedTask])

  // Check if stock is sufficient
  const isStockSufficient = useCallback((mat) => {
    const required = calculateRequired(mat)
    return parseFloat(mat.stock || 0) >= required
  }, [calculateRequired])

  // Check if all stock received
  const checkAllStockReceived = useCallback(() => {
    return selectedTaskMats.every((mat) => mat.stockReceived)
  }, [selectedTaskMats])

  // Handle stock received
  const handleStockReceived = useCallback(async (mat, index) => {
    setSaving(true)
    try {
      const required = calculateRequired(mat)
      const newStock = parseFloat(mat.stock) - required

      await patchProduct({
        resource: [{ id: mat.id, stock: newStock.toFixed(4) }],
      })

      const newMats = [...selectedTaskMats]
      newMats[index] = { ...mat, stockReceived: true, pendingstock: false }
      setSelectedTaskMats(newMats)

      await patchTask({
        resource: [{ id: selectedTask.id, materials: JSON.stringify(newMats) }],
      })

      showSnackbar(t("Stock received successfully"))
    } catch (error) {
      console.error("Error receiving stock:", error)
      showSnackbar(t("Failed to receive stock"), "error")
    } finally {
      setSaving(false)
    }
  }, [calculateRequired, selectedTask, selectedTaskMats, showSnackbar, t])

  // Handle proceed to production
  const handleProceed = useCallback(async () => {
    setSaving(true)
    try {
      await patchTask({
        resource: [{
          id: selectedTask.id,
          materials: JSON.stringify(selectedTaskMats),
          comments: taskComments,
          status: "STOCK-RECEIVED",
        }],
      })

      setSelectedTask({ ...selectedTask, status: "STOCK-RECEIVED" })
      showSnackbar(t("Task ready for production"))
      handleRefresh()
    } catch (error) {
      console.error("Error updating task:", error)
      showSnackbar(t("Failed to update task"), "error")
    } finally {
      setSaving(false)
    }
  }, [selectedTask, selectedTaskMats, taskComments, showSnackbar, handleRefresh, t])

  // Handle finish task
  const handleFinish = useCallback(async () => {
    setSaving(true)
    try {
      await patchTask({
        resource: [{ id: selectedTask.id, status: "FINISHED" }],
      })

      const currentStock = await getProductStock(selectedTask.productid)
      await patchProduct({
        resource: [{
          id: selectedTask.productid,
          stock: String(parseFloat(currentStock) + parseFloat(selectedTask.quantity)),
        }],
      })

      showSnackbar(t("Task completed successfully"))
      setSelectedTask(null)
      setSelectedTaskMats([])
      handleRefresh()
    } catch (error) {
      console.error("Error finishing task:", error)
      showSnackbar(t("Failed to complete task"), "error")
    } finally {
      setSaving(false)
    }
  }, [selectedTask, getProductStock, showSnackbar, handleRefresh, t])

  // Handle create stock order
  const handleCreateStockOrder = useCallback(async (mat, index) => {
    setSaving(true)
    try {
      const orderContent = { ...mat, quantity: calculateRequired(mat) }

      await postInvoice({
        resource: [{
          providerid: 9999,
          importDate: new Date().toISOString(),
          order_ref: selectedTask.orderref,
          section: selectedTask.type,
          type: "REQUEST-PM",
          content: JSON.stringify([orderContent]),
        }],
      })

      const newMats = [...selectedTaskMats]
      newMats[index] = { ...mat, stockOrdered: true }
      setSelectedTaskMats(newMats)

      await patchTask({
        resource: [{ id: selectedTask.id, materials: JSON.stringify(newMats) }],
      })

      showSnackbar(t("Stock order created"))
    } catch (error) {
      console.error("Error creating stock order:", error)
      showSnackbar(t("Failed to create order"), "error")
    } finally {
      setSaving(false)
    }
  }, [calculateRequired, selectedTask, selectedTaskMats, showSnackbar, t])

  // Handle create sub-task
  const handleCreateSubTask = useCallback(async (mat) => {
    setSaving(true)
    try {
      const reqs = JSON.parse(mat.requirements || "[]")
      for (const req of reqs) {
        await postTask({
          resource: [{
            type: req.typid,
            materials: JSON.stringify(req.materials),
            status: "PENDING",
            orderref: selectedTask.orderref,
            taskref: selectedTask.id,
            productid: mat.id,
            name: mat.name,
            description: "INTERNAL-TASK",
            quantity: calculateRequired(mat).toFixed(4),
            expectedDate: selectedTask.expectedDate,
            customer_id_ref: selectedTask.customer_id_ref,
            seq: selectedTask.seq,
          }],
        })
      }

      showSnackbar(t("Sub-task created"))
      handleRefresh()
    } catch (error) {
      console.error("Error creating sub-task:", error)
      showSnackbar(t("Failed to create sub-task"), "error")
    } finally {
      setSaving(false)
    }
  }, [calculateRequired, selectedTask, showSnackbar, handleRefresh, t])

  // Check for pending sub-tasks
  const checkPendingSubTasks = useCallback((mat) => {
    return allTasks.filter(
      (task) =>
        parseFloat(task.taskref) === parseFloat(selectedTask?.id) &&
        task.orderref === selectedTask?.orderref &&
        task.productid === mat.id &&
        task.status !== "FINISHED"
    )
  }, [allTasks, selectedTask])

  // Material replacement
  const handleReplaceMaterial = useCallback((newMaterial) => {
    if (replacingMaterialIndex === null) return

    const newMats = [...selectedTaskMats]
    newMats[replacingMaterialIndex] = {
      ...newMaterial,
      quantity: selectedTaskMats[replacingMaterialIndex].quantity,
    }
    setSelectedTaskMats(newMats)
    setDrawerOpen(false)
    setReplacingMaterialIndex(null)
    showSnackbar(t("Material replaced"))
  }, [replacingMaterialIndex, selectedTaskMats, showSnackbar, t])

  // Filtered materials for drawer
  const filteredMaterials = useMemo(() => {
    if (!materialSearch) {
      return materialsList.filter(
        (m) => m.stock > 0 && m.usageType !== "COMPLETE-PRODUCT"
      )
    }
    const searchRegex = new RegExp(materialSearch, "i")
    return materialsList.filter(
      (m) => searchRegex.test(m.name) || searchRegex.test(m.description)
    )
  }, [materialsList, materialSearch])

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: "name",
      headerName: t("Product"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <CategoryIcon sx={{ color: "#6b7280", fontSize: 18 }} />
          <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
        </Stack>
      ),
    },
    {
      field: "quantity",
      headerName: t("Quantity"),
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} size="small" sx={{ bgcolor: "#e0e7ff", color: "#4338ca" }} />
      ),
    },
    {
      field: "expectedDate",
      headerName: t("Expected Date"),
      width: 130,
    },
    {
      field: "status",
      headerName: t("Status"),
      width: 140,
      renderCell: (params) => <StatusChip status={params.value} t={t} />,
    },
    {
      field: "orderref",
      headerName: t("Order ID"),
      width: 100,
    },
    {
      field: "order.name",
      headerName: t("Customer"),
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.order?.name || "-",
    },
  ], [t])

  // DataGrid styles
  const dataGridSx = useMemo(() => ({
    border: "none",
    "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase",
    },
    "& .MuiDataGrid-row": { cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } },
    "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6", fontSize: "0.875rem" },
  }), [])

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 3, py: 2 }}>
        <Container maxWidth={false} disableGutters>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", color: "#6b7280" }}
                onClick={() => navigate("/customers")}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t("Home")}
              </Link>
              <Link
                component="button"
                underline="hover"
                color="#6b7280"
                onClick={() => navigate("/sections")}
              >
                {t("Sections")}
              </Link>
              <Typography color="#111827" fontWeight={500}>{sectionName}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/sections")}
                sx={{ borderColor: "#e5e7eb", color: "#6b7280" }}
              >
                {t("Back to Sections")}
              </Button>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ height: 2 }} />}

      {/* Order Filter Banner */}
      {orderFilter && (
        <Box sx={{ bgcolor: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
          <Container maxWidth={false}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShoppingCartIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                <Typography variant="body2" color="#1e40af" fontWeight={500}>
                  {t("Filtering by Order")} #{orderFilter}
                </Typography>
                <Chip
                  label={`${sectionTasks.length} ${t("tasks")}`}
                  size="small"
                  sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 500, height: 22 }}
                />
              </Stack>
              <Button
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                onClick={clearOrderFilter}
                sx={{ color: "#3b82f6", textTransform: "none" }}
              >
                {t("Clear Filter")}
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Container maxWidth={false} sx={{ py: 3 }}>
        {/* Analytics Header */}
        <AnalyticsHeader stats={stats} sectionName={sectionName} t={t} />

        {/* Tasks Grid */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6" }}>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Tasks")} ({sectionTasks.length})
            </Typography>
          </Box>
          <DataGrid
            rows={sectionTasks}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => handleTaskSelect(params.row)}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={dataGridSx}
            localeText={{ noRowsLabel: t("No tasks in this section") }}
          />
        </Card>

        {/* Selected Task Detail */}
        {selectedTask && (
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid #f3f4f6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setExpandedTask(!expandedTask)}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "#3b82f6", width: 40, height: 40 }}>
                  <BuildIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#111827">
                    {selectedTask.name}
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Order")} #{selectedTask.orderref} • {t("Quantity")}: {selectedTask.quantity}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusChip status={selectedTask.status} t={t} />
                <IconButton size="small">
                  {expandedTask ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Box>

            <Collapse in={expandedTask}>
              <CardContent sx={{ p: 3 }}>
                {/* Finished Task View */}
                {selectedTask.status === "FINISHED" && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    <Typography variant="subtitle2">
                      {t("Task completed")}
                    </Typography>
                    <Typography variant="body2">
                      {selectedTask.name} • {t("Quantity")}: {selectedTask.quantity}
                    </Typography>
                  </Alert>
                )}

                {/* Active Task View */}
                {selectedTask.status !== "FINISHED" && (
                  <>
                    {/* Operation Mode */}
                    {selectedTask.status === "PENDING" && (
                      <Box sx={{ mb: 3 }}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("Operation Mode")}</FormLabel>
                          <RadioGroup
                            row
                            value={operationMode}
                            onChange={(e) => setOperationMode(e.target.value)}
                          >
                            <FormControlLabel value="normal" control={<Radio />} label={t("Normal Mode")} />
                            <FormControlLabel value="override" control={<Radio />} label={t("Override Mode")} />
                          </RadioGroup>
                        </FormControl>

                        {checkAllStockReceived() && (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleProceed}
                            disabled={saving}
                            sx={{ ml: 2 }}
                          >
                            {t("Proceed to Production")}
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Materials Section */}
                    <Typography variant="subtitle2" fontWeight={600} color="#111827" sx={{ mb: 2 }}>
                      {selectedTask.status === "PENDING"
                        ? t("Materials Required - Get from Stock")
                        : t("Materials Ready - Execute Task")}
                    </Typography>

                    {selectedTaskMats.map((mat, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: mat.stockReceived ? "#86efac" : isStockSufficient(mat) ? "#93c5fd" : "#fcd34d",
                          bgcolor: mat.stockReceived ? "#f0fdf4" : isStockSufficient(mat) ? "#eff6ff" : "#fffbeb",
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CategoryIcon sx={{ color: "#6b7280" }} />
                              <Typography variant="subtitle2">{mat.name}</Typography>
                            </Stack>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            {!mat.stockReceived ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color={isStockSufficient(mat) ? "success.main" : "error.main"}
                                >
                                  {t("Required")}: {selectedTask.quantity} × {mat.quantity} {mat.unit} = {calculateRequired(mat).toFixed(2)} {mat.unit}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t("Stock")}: {mat.stock} {mat.unit}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label={t("Stock on Section")}
                                color="success"
                                size="small"
                              />
                            )}
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {selectedTask.status === "PENDING" && !mat.stockReceived && (
                                <>
                                  {isStockSufficient(mat) && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={<CheckCircleIcon />}
                                      onClick={() => handleStockReceived(mat, index)}
                                      disabled={saving}
                                    >
                                      {t("Stock Received")}
                                    </Button>
                                  )}

                                  {!isStockSufficient(mat) && (
                                    <>
                                      {mat.usageType === "PURCHASED-MATERIAL" && !mat.stockOrdered && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="warning"
                                          startIcon={<AddShoppingCartIcon />}
                                          onClick={() => handleCreateStockOrder(mat, index)}
                                          disabled={saving}
                                        >
                                          {t("Order Stock")}
                                        </Button>
                                      )}

                                      {mat.usageType === "MATERIAL" && checkPendingSubTasks(mat).length === 0 && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          startIcon={<BuildIcon />}
                                          onClick={() => handleCreateSubTask(mat)}
                                          disabled={saving}
                                        >
                                          {t("Create Task")}
                                        </Button>
                                      )}

                                      {checkPendingSubTasks(mat).length > 0 && (
                                        <Chip icon={<WarningIcon />} label={t("Pending Tasks")} color="warning" size="small" />
                                      )}

                                      {mat.stockOrdered && (
                                        <Chip icon={<LocalShippingIcon />} label={t("Stock Ordered")} color="info" size="small" />
                                      )}
                                    </>
                                  )}

                                  {operationMode === "override" && mat.overidable && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<SwapHorizIcon />}
                                      onClick={() => {
                                        setReplacingMaterialIndex(index)
                                        setDrawerOpen(true)
                                      }}
                                      disabled={saving}
                                    >
                                      {t("Replace")}
                                    </Button>
                                  )}
                                </>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    {/* Comments & Finish */}
                    {(selectedTask.status === "PENDING" || selectedTask.status === "STOCK-RECEIVED") && (
                      <Box sx={{ mt: 3 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={t("Comments")}
                          value={taskComments}
                          onChange={(e) => setTaskComments(e.target.value)}
                          sx={{ mb: 3 }}
                        />

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            color="error"
                            size="large"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <DoneAllIcon />}
                            onClick={handleFinish}
                            disabled={saving}
                            sx={{ minWidth: 200, minHeight: 56 }}
                          >
                            {t("Finish Task")}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Collapse>
          </Card>
        )}
      </Container>

      {/* Material Replacement Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{t("Select Replacement Material")}</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search materials...")}
            value={materialSearch}
            onChange={(e) => setMaterialSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <List sx={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}>
            {filteredMaterials.map((mat) => (
              <ListItem
                key={mat.id}
                button
                onClick={() => handleReplaceMaterial(mat)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemIcon>
                  {mat.usageType === "MATERIAL" && <BuildIcon color="primary" />}
                  {mat.usageType === "PURCHASED-MATERIAL" && <ShoppingCartIcon color="secondary" />}
                  {mat.usageType === "COMPLETE-PRODUCT" && <CategoryIcon color="success" />}
                </ListItemIcon>
                <ListItemText
                  primary={mat.name}
                  secondary={`${t("Stock")}: ${mat.stock} ${mat.unit}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default memo(MuiSectionPage)
